// R38 — background photo uploader with on-device compression.
//
// Field report (2026-08-07): a cleaner spent 36 minutes uploading before-photos
// and only 3 of 20 made it. Causes: raw 4-8MB iPhone photos over cellular,
// sequential all-or-nothing 5-file chunks with no timeout/retry, and a server
// that transcodes HEIC at full resolution on the request path. This module
// fixes the class:
//
//   • shrinkForUpload(): decode on-device (iOS decodes HEIC natively in <img>),
//     downscale to ≤1600px long edge, re-encode JPEG q0.82 → ~200-400KB/photo
//     (~15-25× less data) — and a real JPEG bypasses the server's slow
//     HEIC transcode entirely.
//   • enqueuePhotos(): the chip appears IMMEDIATELY ("Preparing…"), then each
//     file is compressed, persisted to IndexedDB, and handed to the pump one
//     by one — uploads pipeline behind compression, and an early exit loses at
//     most the file being compressed. The cleaner puts the phone down.
//   • the pump uploads ONE file per request (small requests never hit the
//     5-min server ceiling; a failure loses one photo's attempt, not a
//     5-photo chunk), THREE requests in parallel (R40 — the photo route is
//     serialized server-side via the mutateTurns chain, so concurrent posts
//     are race-safe), and retries transient failures with backoff. PERMANENT
//     rejections (404 target gone, 413 too large, 400/415/422) are removed
//     and REPORTED on the chip — never retried forever, never silently eaten.
//   • every queued photo carries a client-minted `client_key`; the server
//     dedupes on it, so a retry after "uploaded but the reply never arrived"
//     can't double-post.
//   • the document.body progress chip survives page redraws (same pattern as
//     the shared-actor chip / QuickLogIssue).
//
// Completion events: window 'surge-photo-uploaded' with {url, fields, response}
// — pages listen to refresh their local turn state.
//
// The Supabase bearer rides in via main.ts's global fetch interceptor, so
// uploads MUST run from page JS (never a service worker).
//
// R116 #5 (Jorge: "upload videos as before or after evidence… multiple
// pictures at once") — every evidence picker in the app now rides this queue:
//   • VIDEOS upload ONE at a time (photos stay 3-wide): a 100 MB clip sharing
//     the uplink three ways ran past the server's request timeout and was
//     re-sent forever.
//   • the per-file cap comes from the SERVER (GET /api/assetops/upload-limits;
//     140 MB until it answers) — and a 413 is permanent: dropped + reported.
//   • big files go up over XMLHttpRequest for BYTE progress (fetch has none);
//     XHR skips main.ts's fetch interceptor, so the bearer is attached here.
//   • receipts keep a 2400 px long edge (the codes on them must stay legible).
//   • a file with an empty / generic type is classified by its extension.
//   • pages read the queue per target (pendingItems / pendingCountMatching —
//     e.g. ONE board row's after files) and listen for 'surge-photo-queue' /
//     'surge-photo-progress' / 'surge-photo-uploaded' / 'surge-photo-dropped'.

import { supabase } from './supabase';
import { isVideoFile, fmtBytes } from '../shared/lib/mediaKind';

const DB_NAME = 'surge-photos';
const STORE = 'photos';
const VERSION = 1;

// Client-side mirror of the server's 150MB multer cap (with margin). An
// oversize walkthrough video would stream ~150MB over cellular just to be
// 413'd — every retry. Reject it up front, visibly, instead.
// R116 #5: the SERVER says what fits (UPLOAD_MAX_MB — the storage project's
// limit may be lower than multer's); 140 MB until it answers.
const DEFAULT_MAX_UPLOAD_BYTES = 140 * 1024 * 1024;
let maxUploadBytes = DEFAULT_MAX_UPLOAD_BYTES;
let capLoad: Promise<number> | null = null;
/** The per-file cap the queue enforces right now (bytes). */
export function uploadCapBytes(): number { return maxUploadBytes; }
/** Ask the server once per session for the cap (never blocks more than ~4 s; offline keeps the default and asks again next time). */
export function loadUploadCap(): Promise<number> {
  if (capLoad) return capLoad;
  capLoad = (async () => {
    let ok = false;
    try {
      const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = ctl ? setTimeout(() => ctl.abort(), 4000) : null;
      const r = await fetch('/api/assetops/upload-limits', ctl ? { signal: ctl.signal } : {});
      if (timer) clearTimeout(timer);
      if (r.ok) {
        const j = await r.json().catch(() => null);
        const n = Number(j && j.max_bytes);
        if (Number.isFinite(n) && n >= 1024 * 1024) { maxUploadBytes = n; ok = true; }
      }
    } catch { /* offline / old server — keep the default */ }
    if (!ok) capLoad = null;   // try again on the next pick
    return maxUploadBytes;
  })();
  return capLoad;
}

export interface QueuedPhoto {
  id?: number;
  url: string;                       // endpoint, e.g. /api/assetops/turns/<id>/photo
  name: string;
  blob: Blob;
  fields: Record<string, string>;    // extra form fields (kind, checklist_item, …)
  clientKey: string;                 // idempotency key the server dedupes on
  label: string;
  queuedAt: number;
  attempts: number;
  retryAt?: number;   // R40: transient-failure backoff — workers skip until this passes (in-memory only)
  video?: boolean;    // R116 #5: uploads 1-wide (photos 3-wide)
  size?: number;      // bytes to send
  sent?: number;      // R116 #5: bytes sent on the current attempt (in-memory only)
}

function available(): boolean { return typeof indexedDB !== 'undefined'; }

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(db => new Promise<T>((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    req.onsuccess = () => resolve(req.result as T);
    req.onerror = () => reject(req.error);
    t.oncomplete = () => db.close();
  }));
}

// In-memory mirror of the persisted queue so pages can read pending counts
// synchronously during render. IndexedDB stays the source of truth; records
// that fail to persist still upload from memory (they just don't survive a
// full reload — the chip warns when that's the situation).
let mem: QueuedPhoto[] = [];
let idbDead = false;      // hard-unavailable (private mode) — chip warns
let loaded = false;
let loading: Promise<void> | null = null;

async function ensureLoaded(): Promise<void> {
  if (loaded) return;
  if (!loading) {
    loading = (async () => {
      if (available()) {
        try {
          const persisted = await tx<QueuedPhoto[]>('readonly', s => s.getAll());
          // Merge BEFORE any in-session enqueues (persisted rows have ids;
          // session rows without ids were enqueued while we were loading).
          mem = [...persisted, ...mem.filter(p => p.id == null)];
        } catch { idbDead = true; }
      } else idbDead = true;
      loaded = true;
    })();
  }
  await loading;
}

// ── on-device downscale ─────────────────────────────────────────────────────
const MAX_EDGE = 1600;       // plenty for cleaning documentation + AI grading
const RECEIPT_EDGE = 2400;   // R116 #5: a receipt's line items + codes must stay legible
const JPEG_Q = 0.82;
const SKIP_UNDER = 300_000;  // already small — not worth re-encoding

/**
 * Downscale + re-encode a picked image to JPEG. Videos and small files pass
 * through untouched; ANY failure (undecodable format, canvas limits) falls
 * back to the original file — uploading big beats losing the photo. Browsers
 * apply EXIF orientation during decode (image-orientation: from-image is the
 * default), so the canvas output is upright.
 * iOS Safari decodes HEIC natively here, so the output JPEG also bypasses the
 * server's slow full-resolution HEIC transcode.
 */
export async function shrinkForUpload(file: File, maxEdge: number = MAX_EDGE): Promise<{ blob: Blob; name: string }> {
  if (isVideoFile(file)) return { blob: file, name: file.name };   // R116: by type, else by extension (an untyped .mov)
  if (!/^image\//i.test(file.type || '') && file.type) return { blob: file, name: file.name };   // never re-encode a non-image
  if (file.size <= SKIP_UNDER && /jpe?g|png|webp/i.test(file.type)) return { blob: file, name: file.name };
  const objUrl = URL.createObjectURL(file);
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('decode failed'));
      img.src = objUrl;
    });
    const w = img.naturalWidth, h = img.naturalHeight;
    if (!w || !h) throw new Error('no dimensions');
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    // JPEG has no alpha — composite transparent PNGs (floor plans, scans,
    // logos) onto WHITE, not the canvas default that encodes as black (R47).
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_Q));
    if (!blob || !blob.size) throw new Error('encode failed');
    // A rare pathological encode can come out BIGGER than a small original.
    if (blob.size >= file.size && scale === 1) return { blob: file, name: file.name };
    return { blob, name: file.name.replace(/\.[^.]+$/, '') + '.jpg' };
  } catch {
    return { blob: file, name: file.name };   // e.g. HEIC on desktop Chrome
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

// ── progress chip (document.body — survives page redraws) ───────────────────
let chip: HTMLElement | null = null;
let batchTotal = 0;
let batchDone = 0;
let batchDropped = 0;
let batchVideos = 0;         // R116: clips in this batch — the chip says "files", not "photos"
let preparing = 0;           // files still compressing (not yet in mem)
let lastDropNote = '';       // human note for the most recent permanent failure
let hideTimer: number | null = null;

function chipEl(): HTMLElement {
  if (chip && document.body.contains(chip)) return chip;
  chip = document.createElement('div');
  chip.id = 'photo-up-chip';
  chip.setAttribute('role', 'status');
  chip.setAttribute('aria-live', 'polite');
  chip.style.cssText = 'position:fixed; left:50%; transform:translateX(-50%); bottom:14px; z-index:9999;'
    + 'background:#0f172a; color:#fff; font:600 12.5px/1.4 -apple-system,system-ui,sans-serif;'
    + 'padding:9px 16px; border-radius:999px; box-shadow:0 4px 14px rgba(0,0,0,.28);'
    + 'max-width:92vw; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; display:none;';
  document.body.appendChild(chip);
  return chip;
}

function setChip(text: string, tone: 'busy' | 'ok' | 'warn'): void {
  const el = chipEl();
  el.textContent = text;
  el.style.background = tone === 'ok' ? '#166534' : tone === 'warn' ? '#92400e' : '#0f172a';
  el.style.display = 'block';
  if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
  if (tone !== 'busy') hideTimer = window.setTimeout(() => { if (chip) chip.style.display = 'none'; }, tone === 'ok' ? 4000 : 8000);
}

const noun = (n: number, withVideo: boolean) => withVideo ? `file${n === 1 ? '' : 's'}` : `photo${n === 1 ? '' : 's'}`;

function refreshChip(): void {
  const pending = mem.length;
  const vids = batchVideos > 0 || mem.some(p => p.video);
  if (!pending && !preparing) {
    // Terminal state — honest about anything that could NOT be delivered.
    if (batchDropped > 0) {
      setChip(`⚠ Uploaded ${batchDone} of ${batchTotal}${lastDropNote ? ` — ${lastDropNote}` : ` — ${batchDropped} could not be delivered`}`, 'warn');
    } else if (batchTotal > 0) {
      setChip(`✓ All ${batchTotal} ${noun(batchTotal, vids)} uploaded`, 'ok');
    } else if (chip) chip.style.display = 'none';
    batchTotal = 0; batchDone = 0; batchDropped = 0; batchVideos = 0; lastDropNote = '';
    return;
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    setChip(idbDead
      ? `⏸ ${pending} ${noun(pending, vids)} waiting — offline storage unavailable, KEEP THIS PAGE OPEN`
      : `⏸ ${pending} ${noun(pending, vids)} saved — will upload when you're back online`, 'warn');
  } else if (preparing > 0) {
    setChip(`📤 Preparing ${preparing} ${noun(preparing, vids)} — ${batchDone} of ${batchTotal} uploaded`, 'busy');
  } else {
    // R116: a clip in flight shows its own byte progress ("video 46%")
    const v = [...inFlight].find(p => p.video && p.size);
    const pct = v ? Math.min(99, Math.round(((v.sent || 0) / (v.size || 1)) * 100)) : -1;
    setChip(`📤 Uploading ${vids ? `${batchTotal} ${noun(batchTotal, true)}` : 'photos'} — ${batchDone} of ${batchTotal} done${pct >= 0 ? ` · video ${pct}%` : ''}`, 'busy');
  }
}

// ── page signals (R116) ─────────────────────────────────────────────────────
function emit(name: string, detail: any): void {
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* non-browser */ }
}
/** The queue changed (added / finished / dropped) — pickers redraw their pending tiles. */
function queueChanged(): void { emit('surge-photo-queue', { count: mem.length }); }
const lastProgressAt = new Map<string, number>();
function progressed(rec: QueuedPhoto): void {
  const now = Date.now();
  if (now - (lastProgressAt.get(rec.clientKey) || 0) < 200 && (rec.sent || 0) < (rec.size || 0)) return;
  lastProgressAt.set(rec.clientKey, now);
  emit('surge-photo-progress', { url: rec.url, fields: rec.fields, clientKey: rec.clientKey, sent: rec.sent || 0, total: rec.size || rec.blob.size });
  if (rec.video) refreshChip();
}

// ── queue + pump ────────────────────────────────────────────────────────────
function newKey(): string {
  return 'pk_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

export interface EnqueueOpts {
  /** Long-edge cap for the on-device downscale (default 1600; receipts 2400). */
  maxEdge?: number;
}

/**
 * Compress + persist photos with IMMEDIATE feedback, then return — uploads
 * continue in the background (and across restarts). The chip appears before
 * the first compression; each file starts uploading as soon as it's ready.
 * This is the ONLY entry point pages use. Resolves to the number of files
 * accepted into the queue (a too-large one is refused, reported, not counted).
 */
export async function enqueuePhotos(
  url: string,
  files: File[],
  fields: Record<string, string>,
  label: string,
  opts: EnqueueOpts = {},
): Promise<number> {
  const list = Array.from(files || []);
  if (!list.length) return 0;
  preparing += list.length;
  batchTotal += list.length;
  batchVideos += list.filter(isVideoFile).length;
  refreshChip();
  await ensureLoaded();
  await loadUploadCap();
  const edge = opts.maxEdge || (fields && fields.kind === 'receipt' ? RECEIPT_EDGE : MAX_EDGE);
  let accepted = 0;
  for (const f of list) {
    const video = isVideoFile(f);
    const { blob, name } = await shrinkForUpload(f, edge);
    preparing = Math.max(0, preparing - 1);
    // An oversize file (uncompressible walkthrough video) would stream its
    // whole body over cellular just to be 413'd, on every retry. Refuse it
    // up front, visibly.
    if (blob.size > maxUploadBytes) {
      batchDropped++;
      lastDropNote = `"${name.slice(0, 40)}" is ${fmtBytes(blob.size)} — the limit is ${fmtBytes(maxUploadBytes)}; ${video ? 'record a shorter clip (about 2 min)' : 'it is too large to upload'}`;
      emit('surge-photo-dropped', { url, fields, label, status: 413, note: lastDropNote, name });
      refreshChip();
      continue;
    }
    const rec: QueuedPhoto = {
      url, name, blob, fields, clientKey: newKey(), label,
      queuedAt: Date.now(), attempts: 0, video, size: blob.size,
    };
    if (available() && !idbDead) {
      // Per-record persistence: one failed add leaves just THIS record
      // memory-only; the next file still tries IndexedDB.
      try {
        const id = await tx<IDBValidKey>('readwrite', s => s.add(rec));
        rec.id = id as number;
      } catch { /* memory-only record */ }
    }
    mem.push(rec);
    accepted++;
    refreshChip();
    queueChanged();
    kick();          // pipeline: upload file 1 while file 2 compresses
  }
  refreshChip();
  return accepted;
}

/** Pending uploads whose endpoint contains `urlPart` — for per-card badges. */
export function pendingCountFor(urlPart: string): number {
  return mem.filter(p => p.url.includes(urlPart)).length;
}
/** Pending uploads for an endpoint that also carry fields[field] === value —
 * e.g. the ONE logged issue whose verify photo is still in flight. */
export function pendingCountWhere(urlPart: string, field: string, value: string): number {
  return mem.filter(p => p.url.includes(urlPart) && (p.fields || {})[field] === value).length;
}
/** R116 #5: the predicate form — one endpoint serves every board row, so a
 * per-row "after uploading" chip matches board_id AND kind. */
export function pendingCountMatching(urlPart: string, pred: (fields: Record<string, string>) => boolean): number {
  return mem.filter(p => p.url.includes(urlPart) && pred(p.fields || {})).length;
}
export function pendingCount(): number { return mem.length; }

export interface PendingItem {
  key: string; name: string; video: boolean; size: number; sent: number;
  state: 'uploading' | 'queued' | 'waiting';
  blob: Blob; fields: Record<string, string>;
}
/** R116 #5: the queued files for one target (pickers draw a tile per file, with progress). */
export function pendingItems(urlPart: string, pred?: (fields: Record<string, string>) => boolean): PendingItem[] {
  return mem.filter(p => p.url.includes(urlPart) && (!pred || pred(p.fields || {}))).map(p => ({
    key: p.clientKey, name: p.name, video: !!p.video, size: p.size || p.blob.size, sent: inFlight.has(p) ? (p.sent || 0) : 0,
    state: inFlight.has(p) ? 'uploading' : (p.retryAt && p.retryAt > Date.now()) || (typeof navigator !== 'undefined' && navigator.onLine === false) ? 'waiting' : 'queued',
    blob: p.blob, fields: p.fields || {},
  }));
}

async function removeRec(rec: QueuedPhoto): Promise<void> {
  mem = mem.filter(p => p !== rec);
  lastProgressAt.delete(rec.clientKey);
  // Always attempt the IDB delete when the record was persisted — a stale
  // idbDead latch must not leave ghost rows that re-upload next session.
  if (rec.id != null) { try { await tx('readwrite', s => s.delete(rec.id!)); } catch { /* memory copy already gone */ } }
  queueChanged();
}

let backoffTimer: number | null = null;
const inFlight = new Set<QueuedPhoto>();
const CONCURRENCY = 3;
let activeWorkers = 0;

function kick(): void {
  if (backoffTimer) { clearTimeout(backoffTimer); backoffTimer = null; }
  // A real wake signal (online / back-to-visible / new enqueue) retries
  // deferred items immediately — the stamps only guard against pool siblings
  // re-claiming a just-failed rec with zero delay.
  for (const p of mem) delete p.retryAt;
  void pump();
}

function scheduleKick(ms: number): void {
  if (backoffTimer) clearTimeout(backoffTimer);
  backoffTimer = window.setTimeout(() => { backoffTimer = null; void pump(); }, ms);
}

// R40: a small worker pool instead of one-at-a-time — with ~300KB compressed
// files, round-trip latency dominates, so 3 parallel requests ≈ 3× the
// throughput on a 20-photo batch. A worker that hits a TRANSIENT failure
// backs the whole pool off (rotating its item to the back); permanent
// failures and successes drain normally.
// R116 #5: at most ONE video in flight — a clip gets the whole uplink (its
// request must finish inside the server's timeout); photos keep 3-wide. And
// the pool now GROWS on every kick: files arrive one by one as they are
// prepared, and the old "rerun after the pool drains" left a batch 1-wide
// behind its first upload (a long clip held every photo behind it).
const offline = () => typeof navigator !== 'undefined' && navigator.onLine === false;
function videoBusy(): boolean { for (const p of inFlight) if (p.video) return true; return false; }
/** Take the next ready file (synchronously, so two workers never take the same one). */
function claim(): QueuedPhoto | null {
  const now = Date.now();
  const rec = mem.find(p => !inFlight.has(p) && (!p.retryAt || p.retryAt <= now) && !(p.video && videoBusy()));
  if (!rec) return null;
  inFlight.add(rec);
  rec.sent = 0;
  return rec;
}
async function pump(): Promise<void> {
  await ensureLoaded();
  if (offline()) { refreshChip(); return; }
  while (activeWorkers < CONCURRENCY) {
    const rec = claim();
    if (!rec) break;
    activeWorkers++;
    void work(rec).finally(() => { activeWorkers--; refreshChip(); });
  }
  refreshChip();
}
async function work(first: QueuedPhoto): Promise<void> {
  let rec: QueuedPhoto | null = first;
  while (rec) {
    try {
      const outcome = await uploadOne(rec);
      if (outcome === 'ok') {
        await removeRec(rec);
        batchDone++;
        refreshChip();
      } else if (outcome === 'fail') {   // permanent — remove AND report, never retry
        await removeRec(rec);
        batchDropped++;
        refreshChip();
      } else {
        // transient: rotate to the back so one bad upload can't starve
        // the batch, stamp its backoff so sibling workers can't re-claim
        // it instantly, and retire this worker (the others finish their
        // items; the timer resumes the rest).
        rec.attempts++;
        const delay = Math.min(5000 * Math.pow(2, Math.min(rec.attempts, 6)), 300_000);
        rec.retryAt = Date.now() + delay;
        const i = mem.indexOf(rec);
        if (i >= 0) { mem.splice(i, 1); mem.push(rec); }
        refreshChip();
        queueChanged();
        scheduleKick(delay);
        return;
      }
    } finally {
      inFlight.delete(rec);
    }
    if (offline()) { refreshChip(); return; }
    rec = claim();
    // a clip just finished — let idle slots pick up the photos it was holding back
    if (rec || mem.length) void pump();
  }
}

// R116 #5: files over this go up over XHR for byte progress (fetch has no
// upload progress); small photos keep the cheaper fetch path.
const XHR_OVER = 1_500_000;
type Sent = { status: number; json: any };
async function sendFetch(rec: QueuedPhoto, fd: FormData): Promise<Sent> {
  const r = await fetch(rec.url, { method: 'POST', body: fd });
  const j = await r.json().catch(() => null);
  return { status: r.status, json: j };
}
async function sendXhr(rec: QueuedPhoto, fd: FormData): Promise<Sent> {
  // XHR bypasses main.ts's fetch interceptor — attach the bearer ourselves.
  let token = '';
  if (rec.url.startsWith('/api/')) {
    try { const { data: { session } } = await supabase.auth.getSession(); token = session?.access_token || ''; } catch { /* proceed without — the server answers 401, retried */ }
  }
  return new Promise<Sent>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open('POST', rec.url);
    if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.upload.onprogress = (e) => { if (e.lengthComputable) { rec.sent = Math.min(e.loaded, rec.size || e.total); progressed(rec); } };
    x.onload = () => { let j: any = null; try { j = JSON.parse(x.responseText); } catch { j = null; } resolve({ status: x.status, json: j }); };
    x.onerror = () => reject(new Error('network'));
    x.onabort = () => reject(new Error('aborted'));
    x.ontimeout = () => reject(new Error('timeout'));
    x.send(fd);
  });
}

async function uploadOne(rec: QueuedPhoto): Promise<'ok' | 'fail' | 'retry'> {
  try {
    const fd = new FormData();
    for (const [k, v] of Object.entries(rec.fields || {})) fd.append(k, v);
    fd.append('client_key', rec.clientKey);
    fd.append('photo', rec.blob, rec.name);
    const big = (rec.size || rec.blob.size) > XHR_OVER && typeof XMLHttpRequest !== 'undefined';
    const r = big ? await sendXhr(rec, fd) : await sendFetch(rec, fd);
    if (r.status >= 200 && r.status < 300) {
      rec.sent = rec.size || rec.blob.size;
      emit('surge-photo-uploaded', { url: rec.url, fields: rec.fields, response: r.json, clientKey: rec.clientKey, video: !!rec.video });
      return 'ok';
    }
    // R114: a 404 is permanent ONLY when the server CONFIRMED the turn is
    // gone ({gone:true} — answered after a strict read). A bare 404 (an old
    // server's lax pre-check that read [] on a Supabase blip, a route not
    // mounted mid-deploy, a proxy page) is retried: deleting the photo on it
    // permanently lost a cleaner's only copy.
    if (r.status === 404 && !(r.json && r.json.gone === true)) return 'retry';
    // Permanent rejections: 404 the turn/issue is confirmed gone; 413 too
    // large; 400/415/422 the server will never accept this body. Retrying
    // re-sends the full blob over cellular forever — remove and REPORT instead.
    if (r.status === 404 || r.status === 413 || r.status === 400 || r.status === 415 || r.status === 422) {
      // R116: the server says what fits — remember it for the next pick
      const srvMax = Number(r.json && r.json.max_bytes);
      if (r.status === 413 && Number.isFinite(srvMax) && srvMax >= 1024 * 1024) maxUploadBytes = srvMax;
      lastDropNote = r.status === 404 ? `a ${rec.label} had nowhere to go (it was removed)`
        : r.status === 413 ? `"${rec.name.slice(0, 40)}" (${fmtBytes(rec.size || rec.blob.size)}) is too large${Number.isFinite(srvMax) && srvMax > 0 ? ` — the limit is ${fmtBytes(srvMax)}` : ''}${rec.video ? '; record a shorter clip' : ''}`
        : `a ${rec.label} was rejected (${r.status})`;
      console.warn('[photoUploader] permanent rejection', r.status, rec.label, rec.url);
      emit('surge-photo-dropped', { url: rec.url, fields: rec.fields, label: rec.label, status: r.status, note: lastDropNote, clientKey: rec.clientKey, name: rec.name });   // R112: the job card shows "N photos rejected — retake"
      return 'fail';
    }
    // 401 expired session, 408 timeout, 5xx, HTML error pages → transient, retry.
    return 'retry';
  } catch {
    return 'retry';
  }
}

// ── wake-up triggers ────────────────────────────────────────────────────────
// online: connectivity returned. pageshow: bfcache restore (iOS never refires
// 'load'). visibilitychange→visible: the cleaner came back from the camera
// app / lock screen — exactly when suspended uploads should resume.
if (typeof window !== 'undefined') {
  window.addEventListener('online', kick);
  window.addEventListener('pageshow', kick);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') kick(); });
  // Resume anything persisted from a previous session shortly after boot.
  // Seed counters ADDITIVELY — an enqueue may already be in flight.
  window.setTimeout(() => {
    void ensureLoaded().then(() => {
      const unaccounted = (batchDone + batchDropped + mem.length + preparing) - batchTotal;
      if (unaccounted > 0) batchTotal += unaccounted;
      if (mem.length) { refreshChip(); kick(); }
    });
  }, 2500);
}
